/**
 * URL slug generation utilities for drug and interaction pages
 * Used for SEO-friendly URLs with full drug information
 */

// ─── /baza-lekow URL parsing and building ───────────────────────────────────

export const BAZA_LEKOW_PREFIX = '/baza-lekow';
export type PanelType = 'chpl' | 'ulotka';
export type BazaLekowUrlType =
  | 'home'
  | 'single-drug'
  | 'chpl'
  | 'ulotka'
  | 'interakcje';

export interface ParsedBazaLekowUrl {
  type: BazaLekowUrlType;
  drugSlugs: string[];
  drugIds: string[];
}

function isDrugSlug(segment: string): boolean {
  return /^\d+$/.test(segment) || /-\d+$/.test(segment);
}

function extractDrugIdFromSlug(slug: string): string | null {
  if (/^\d+$/.test(slug)) return slug;
  const match = slug.match(/-(\d+)$/);
  return match ? match[1]! : null;
}

function extractDrugIds(segments: string[]): {
  slugs: string[];
  ids: string[];
} {
  const slugs = segments.filter(isDrugSlug);
  const ids = slugs
    .map(extractDrugIdFromSlug)
    .filter((id): id is string => id !== null);
  return { slugs, ids };
}

/**
 * Parse [[...slugs]] catch-all segments from /baza-lekow route.
 *
 * URL types determined by first segment:
 * - "chpl"       -> single drug ChPL page:   /baza-lekow/chpl/{slug}
 * - "ulotka"     -> single drug Ulotka page:  /baza-lekow/ulotka/{slug}
 * - "interakcje" -> multi-drug interactions:   /baza-lekow/interakcje/{slug1}/{slug2}/...
 * - drug slug    -> single drug base page:     /baza-lekow/{slug}
 * - empty        -> home page:                 /baza-lekow
 */
export function parseBazaLekowSlugs(
  slugs: string[] | undefined,
): ParsedBazaLekowUrl {
  const home: ParsedBazaLekowUrl = {
    type: 'home',
    drugSlugs: [],
    drugIds: [],
  };

  if (!slugs || slugs.length === 0) return home;

  const first = slugs[0]!;

  if (first === 'chpl' || first === 'ulotka') {
    const rest = slugs.slice(1);
    const { slugs: drugSlugs, ids } = extractDrugIds(rest);
    return { type: first, drugSlugs, drugIds: ids };
  }

  if (first === 'interakcje') {
    const rest = slugs.slice(1);
    const { slugs: drugSlugs, ids } = extractDrugIds(rest);
    return { type: 'interakcje', drugSlugs, drugIds: ids };
  }

  if (isDrugSlug(first)) {
    const { slugs: drugSlugs, ids } = extractDrugIds(slugs);
    return { type: 'single-drug', drugSlugs, drugIds: ids };
  }

  return home;
}

/**
 * Build /baza-lekow URL from drugs (database row format).
 * - 0 drugs: /baza-lekow
 * - 1 drug:  /baza-lekow/{slug}-{id}
 * - 2+ drugs: /baza-lekow/interakcje/{slug1}-{id1}/{slug2}-{id2}/...
 */
export function buildBazaLekowUrl(drugs: DrugRowForSlug[]): string {
  if (drugs.length === 0) return BAZA_LEKOW_PREFIX;

  const sorted = [...drugs].sort((a, b) => Number(a.id) - Number(b.id));
  const segments = sorted.map((d) => `${generateDrugSlugFromRow(d)}-${d.id}`);

  if (sorted.length === 1) {
    return `${BAZA_LEKOW_PREFIX}/${segments[0]}`;
  }

  return `${BAZA_LEKOW_PREFIX}/interakcje/${segments.join('/')}`;
}

/**
 * Build /baza-lekow panel URL for a single drug.
 * /baza-lekow/chpl/{slug}-{id}  or  /baza-lekow/ulotka/{slug}-{id}
 */
export function buildBazaLekowPanelUrl(
  panelType: PanelType,
  drug: DrugRowForSlug,
): string {
  const segment = `${generateDrugSlugFromRow(drug)}-${drug.id}`;
  return `${BAZA_LEKOW_PREFIX}/${panelType}/${segment}`;
}

/**
 * Build /baza-lekow URL from just drug IDs (without full slug info).
 * - 1 ID:  /baza-lekow/{id}
 * - 2+ IDs: /baza-lekow/interakcje/{id1}/{id2}/...
 */
export function buildBazaLekowUrlFromIds(drugIds: string[]): string {
  if (drugIds.length === 0) return BAZA_LEKOW_PREFIX;

  const sorted = [...drugIds].sort((a, b) => Number(a) - Number(b));

  if (sorted.length === 1) {
    return `${BAZA_LEKOW_PREFIX}/${sorted[0]}`;
  }

  return `${BAZA_LEKOW_PREFIX}/interakcje/${sorted.join('/')}`;
}

// ─── Legacy URL utilities ───────────────────────────────────────────────────

/**
 * Drug data needed for slug generation
 */
export interface DrugForSlug {
  id: string;
  productName: string;
  commonName?: string | null;
  strength?: string | null;
  form?: string | null;
}

/**
 * Database row format for drugs (snake_case)
 */
export interface DrugRowForSlug {
  id: string;
  product_name: string;
  common_name?: string | null;
  strength?: string | null;
  form?: string | null;
}

/**
 * Normalize text for URL slug
 * - Lowercase
 * - Remove Polish diacritics
 * - Replace non-alphanumeric with dashes
 * - Collapse multiple dashes
 * - Trim leading/trailing dashes
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Truncate a slugified string at a word (dash) boundary.
 * Returns the longest prefix that fits within maxLen without cutting mid-word.
 */
function truncateSlug(slug: string, maxLen: number): string {
  if (slug.length <= maxLen) return slug;
  const truncated = slug.slice(0, maxLen);
  const lastDash = truncated.lastIndexOf('-');
  return lastDash > 0 ? truncated.slice(0, lastDash) : truncated;
}

const COMMON_NAME_MAX = 30;
const STRENGTH_MAX = 20;
const FORM_MAX = 30;

/**
 * Generate full SEO slug from drug data.
 * productName is kept in full; commonName, strength and form are
 * individually truncated at word boundaries to keep URLs reasonable.
 *
 * @example
 * generateDrugSlug({ productName: "Amlozek", commonName: "Amlodypina", strength: "5 mg", form: "tabletki" })
 * // Returns: "amlozek-amlodypina-5-mg-tabletki"
 */
export function generateDrugSlug(drug: DrugForSlug): string {
  const name = slugify(drug.productName);

  const extras: string[] = [];
  if (drug.commonName)
    extras.push(truncateSlug(slugify(drug.commonName), COMMON_NAME_MAX));
  if (drug.strength)
    extras.push(truncateSlug(slugify(drug.strength), STRENGTH_MAX));
  if (drug.form) extras.push(truncateSlug(slugify(drug.form), FORM_MAX));

  return [name, ...extras].join('-');
}

/**
 * Generate full SEO slug from database row format (snake_case)
 */
export function generateDrugSlugFromRow(drug: DrugRowForSlug): string {
  return generateDrugSlug({
    id: drug.id,
    productName: drug.product_name,
    commonName: drug.common_name,
    strength: drug.strength,
    form: drug.form,
  });
}

/**
 * Shorten UUID for URL use
 * Takes first 8 characters (without dashes) for compact URLs
 *
 * @example
 * shortenUuid("f47ac10b-58cc-4372-a567-0e02b2c3d479")
 * // Returns: "f47ac10b"
 */
export function shortenUuid(uuid: string): string {
  return uuid.replace(/-/g, '').substring(0, 8);
}

/**
 * Build URL for a drug page
 *
 * @example
 * buildDrugUrl({ id: "100083610", productName: "Amlozek", ... })
 * // Returns: "/lek/100083610/amlozek-amlodypina-5-mg-tabletki"
 */
export function buildDrugUrl(drug: DrugForSlug): string {
  return `/lek/${drug.id}/${generateDrugSlug(drug)}`;
}

/**
 * Build URL for a drug page from database row format
 */
export function buildDrugUrlFromRow(drug: DrugRowForSlug): string {
  return `/lek/${drug.id}/${generateDrugSlugFromRow(drug)}`;
}

/**
 * Build URL for an interaction page
 * Uses shortened UUID from interaction_cache.id
 *
 * @example
 * buildInteractionUrl("f47ac10b-58cc-4372-...", drug1, drug2)
 * // Returns: "/interakcje/f47ac10b/amlozek-amlodypina-5-mg/klacid-klarytromycyna-500-mg"
 */
export function buildInteractionUrl(
  cacheId: string,
  drug1: DrugForSlug,
  drug2: DrugForSlug,
): string {
  const shortId = shortenUuid(cacheId);
  const slug1 = generateDrugSlug(drug1);
  const slug2 = generateDrugSlug(drug2);

  return `/interakcje/${shortId}/${slug1}/${slug2}`;
}

/**
 * Build URL for an interaction page from database row format
 */
export function buildInteractionUrlFromRow(
  cacheId: string,
  drug1: DrugRowForSlug,
  drug2: DrugRowForSlug,
): string {
  return buildInteractionUrl(
    cacheId,
    {
      id: drug1.id,
      productName: drug1.product_name,
      commonName: drug1.common_name,
      strength: drug1.strength,
      form: drug1.form,
    },
    {
      id: drug2.id,
      productName: drug2.product_name,
      commonName: drug2.common_name,
      strength: drug2.strength,
      form: drug2.form,
    },
  );
}

/**
 * Find interaction cache entry by short ID prefix
 * Used when looking up by 8-char short ID from URL
 *
 * @param shortId - 8-character prefix of UUID
 * @returns SQL LIKE pattern for matching
 */
export function getShortIdPattern(shortId: string): string {
  // Reconstruct UUID pattern with dashes for LIKE query
  // Short ID is first 8 chars without dashes
  // Full UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  // So first 8 chars map to first segment
  return `${shortId}-%`;
}

/**
 * Generate a deterministic short ID for a combination of drug IDs.
 * The ID is deterministic (same inputs always produce same output).
 *
 * Algorithm:
 * 1. Sort drug IDs numerically
 * 2. Join with comma
 * 3. Create a simple hash
 * 4. Convert to base36 for compact URL-safe representation
 *
 * @example
 * generateInteractionId(["100074640", "100033462"])
 * // Returns: "k7x2m9" (deterministic 6-8 char string)
 */
export function generateInteractionId(drugIds: string[]): string {
  // Sort numerically for deterministic ordering
  const sortedIds = [...drugIds].sort((a, b) => Number(a) - Number(b));
  const combined = sortedIds.join(',');

  // Simple hash function (djb2 algorithm)
  let hash = 5381;
  for (let i = 0; i < combined.length; i++) {
    hash = (hash * 33) ^ combined.charCodeAt(i);
  }

  // Convert to unsigned 32-bit and then to base36
  const unsignedHash = hash >>> 0;
  return unsignedHash.toString(36);
}

/**
 * Parse interaction ID from URL and extract drug IDs.
 * This validates that the provided ID matches the expected hash for the drug IDs.
 *
 * @param id - The short interaction ID from URL
 * @param drugIds - The drug IDs from URL
 * @returns true if ID matches, false otherwise
 */
export function validateInteractionId(id: string, drugIds: string[]): boolean {
  const expectedId = generateInteractionId(drugIds);
  return id === expectedId;
}

/**
 * Build a multi-drug interaction URL with deterministic ID and full slugs.
 *
 * @example
 * buildMultiDrugInteractionUrl([drug1, drug2])
 * // Returns: "/interakcje/k7x2m9/amlozek-amlodypina-5-mg-100074640/klacid-klarytromycyna-500-mg-100083610"
 */
export function buildMultiDrugInteractionUrl(drugs: DrugRowForSlug[]): string {
  if (drugs.length < 2) {
    throw new Error('At least 2 drugs are required for an interaction URL');
  }

  // Sort by ID for deterministic URL ordering
  const sortedDrugs = [...drugs].sort((a, b) => Number(a.id) - Number(b.id));
  const drugIds = sortedDrugs.map((d) => d.id);
  const id = generateInteractionId(drugIds);

  const slugSegments = sortedDrugs.map(
    (d) => `${generateDrugSlugFromRow(d)}-${d.id}`,
  );

  return `/interakcje/${id}/${slugSegments.join('/')}`;
}

/**
 * Build a multi-drug interaction URL from just drug IDs (simpler version).
 * Use when you only have IDs without full drug data.
 *
 * @example
 * buildInteractionUrlFromIds(["100033462", "100074640"])
 * // Returns: "/interakcje/k7x2m9/100033462/100074640"
 */
export function buildInteractionUrlFromIds(drugIds: string[]): string {
  if (drugIds.length < 2) {
    throw new Error('At least 2 drugs are required for an interaction URL');
  }

  // Sort numerically for deterministic ordering
  const sortedIds = [...drugIds].sort((a, b) => Number(a) - Number(b));
  const id = generateInteractionId(sortedIds);

  return `/interakcje/${id}/${sortedIds.join('/')}`;
}
